import { Dispatch, FC, SetStateAction } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { HorizontalStepper } from '@medtronic/pecc-react-component-library-js';
import { useHasLockedStatus } from '~/hooks/dataset';
import { AddUserIcon, DataTagIcon, DatasetDetailsIcon } from '~/icons';
import { dataTagsSchema, datasetDetailsSchema } from '~/validation';
import { activeAvatarSx, disabledAvatarSx, stepLabelSx, stepperSx } from './styles';
import { DatasetCreationStepsEnum } from './types';

interface DatasetStepperProps {
  activeStep: DatasetCreationStepsEnum;
  setActiveStep: Dispatch<SetStateAction<DatasetCreationStepsEnum>>;
}

const DatasetStepper: FC<DatasetStepperProps> = ({ activeStep, setActiveStep }) => {
  const { watch } = useFormContext();
  const { id } = useParams();
  const { hasLockedStatus } = useHasLockedStatus();

  const values = watch();
  const validationContext = { isEdit: !!id, hasLockedStatus };

  const isDetailsValid = datasetDetailsSchema.isValidSync(values, { context: validationContext });
  const isDataTagsValid = dataTagsSchema.isValidSync(values, { context: validationContext });

  const isDataTagsStepDisabled = activeStep < DatasetCreationStepsEnum.DATA_TAGS && !isDetailsValid;
  const isDataMembersStepDisabled =
    activeStep < DatasetCreationStepsEnum.DATA_MEMBERS && !(isDetailsValid && isDataTagsValid);

  const disabledTooltipText = 'Fill in all required fields in previous step to move to this step';

  // Navy for the active step and everything before it, gray for the rest
  const activeConnectorSx = {
    [`& .MuiStep-root:nth-of-type(-n + ${activeStep + 1}) .MuiStepConnector-line`]: {
      borderColor: 'navyBlue.main',
    },
    [`& .MuiStep-root:nth-of-type(-n + ${activeStep}) .MuiAvatar-root`]: {
      borderColor: 'navyBlue.main',
      color: 'navyBlue.main',
    },
  };

  const commonStepConfig = {
    hideStepButton: true,
    activeAvatarSx,
    disabledAvatarSx,
    tooltipProps: { placement: 'right-end' as const },
  };

  return (
    <HorizontalStepper
      activeStepNumber={activeStep}
      isLinear={false}
      stepLabelSx={stepLabelSx}
      stepperSx={{ ...stepperSx, ...activeConnectorSx }}
      onStepClick={(stepIndex) => {
        setActiveStep(stepIndex as DatasetCreationStepsEnum);
      }}
      stepsConfig={[
        {
          ...commonStepConfig,
          stepLabel: 'Dataset details',
          stepIcon: <DatasetDetailsIcon />,
        },
        {
          ...commonStepConfig,
          stepLabel: 'Data tags',
          stepIcon: <DataTagIcon />,
          disabled: isDataTagsStepDisabled,
          tooltipText: isDataTagsStepDisabled ? disabledTooltipText : undefined,
        },
        {
          ...commonStepConfig,
          stepLabel: 'Data stewards & uploaders',
          stepIcon: <AddUserIcon />,
          disabled: isDataMembersStepDisabled,
          tooltipText: isDataMembersStepDisabled ? disabledTooltipText : undefined,
        },
      ]}
    />
  );
};

export default DatasetStepper;
